import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Collapse,
  Drawer,
  useMediaQuery
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CancelIcon from '@mui/icons-material/Cancel';
import { ArrowBack } from "@mui/icons-material";
import styles from "./SolutionsPage.module.css";
import { useNavigate } from "react-router-dom";
import default_theme from "assets/themes/themes";

const RuralDevelopmentPage = () => {
  const [search, setSearch] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');
  const navigate = useNavigate();

  const cards = [
    {
      title: "Solutions Catalogue",
      description: "A curated list of rural development solutions and best practices.",
      tags: ["RDD"],
      path: "/solutions/rural-development/solutions-catalog"
    },
    {
      title: "Local Action Plans",
      description: "A curated list of local action plans and implementation strategies.",
      tags: ["LAP"],
      path: "/solutions/rural-development/lap-catalog"
    }
  ];

  const handleFilterToggle = () => {
    if (isMobile) setMobileFilterOpen(true);
    else setIsFilterExpanded((prev) => !prev);
  };

  const toggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleDeleteTag = (tagToDelete) => {
    setSelectedTags((prev) => prev.filter((tag) => tag !== tagToDelete));
  };

  const renderFilterPanel = () => (
    <Box sx={{
      flex: 1,
      minHeight: 0,
      height: '100%',
      display: 'flex',
    }}
    className={styles.filterPanel}>
      <Typography className={styles.filterPanelTitle}>
        Filter by Tags
      </Typography>
      <Box className={styles.filterTags}>
        <Box
          className={`${styles.filterTag} ${selectedTags.includes("RDD") ? styles.selected : ""}`}
          onClick={() => toggleTag("RDD")}
        >
          RDD
        </Box>
      </Box>
    </Box>
  );

  const filteredCards = cards.filter(card =>
    (selectedTags.length === 0 || card.tags.some(tag => selectedTags.includes(tag))) &&
    (card.title.toLowerCase().includes(search.toLowerCase()) ||
      card.description.toLowerCase().includes(search.toLowerCase()) ||
      card.tags.some(tag => tag.toLowerCase().includes(search.toLowerCase()))
    )
  );

  return (
    <Box className={styles.solutionContainer}>
      <Container maxWidth="lg" className={styles.mainContainer}>
        <Box className={styles.contentWrapper}>
          <Box className={styles.headerSection}>
            <button className={styles.backButton} onClick={() => navigate('/solutions')}>
              <ArrowBack />
              Back to Solutions
            </button>
            <Typography variant="h2" className={styles.pageTitle}>
              Rural Development
            </Typography>
          </Box>
          <Box className={styles.searchSection}>
            <Box className={styles.searchContainer}>
              <TextField
                fullWidth
                placeholder="Search ..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: default_theme.palette.font.darkGray }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: default_theme.palette.primary.main,
                  borderRadius: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: default_theme.palette.primary.main,
                    '& fieldset': { borderColor: default_theme.palette.border.subtle },
                    '&:hover fieldset': { borderColor: default_theme.palette.border.subtleHover },
                    '&.Mui-focused fieldset': { borderColor: default_theme.palette.primary.green },
                  },
                  '& .MuiInputBase-input': { color: default_theme.palette.font.darkGray }
                }}
              />
              <Tooltip title="Filter by tags" arrow placement="top">
                <IconButton
                  className={`${styles.filterButton} ${isFilterExpanded || mobileFilterOpen ? styles.filterButtonActive : ''}`}
                  onClick={handleFilterToggle}
                >
                  {isFilterExpanded || mobileFilterOpen ? <LocalOfferIcon /> : <LocalOfferOutlinedIcon />}
                </IconButton>
              </Tooltip>
            </Box>
            {!isMobile && (
              <Collapse in={isFilterExpanded}>
                {renderFilterPanel()}
              </Collapse>
            )}
            <Drawer
              anchor="bottom"
              open={mobileFilterOpen}
              onClose={() => setMobileFilterOpen(false)}
              classes={{ paper: styles.filterDrawerMobile }}
            >
              {renderFilterPanel()}
            </Drawer>
            {selectedTags.length > 0 && (
              <Box className={styles.selectedFilters}>
                {selectedTags.map((tag) => (
                  <Box key={tag} className={styles.filterChip}>
                    {tag}
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteTag(tag)}
                      sx={{ marginLeft: 0.5 }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 3 }}>
            {filteredCards.map((card, idx) => (
              <Box
                key={idx}
                sx={{
                  background: default_theme.palette.primary.main,
                  borderRadius: 3,
                  boxShadow: 3,
                  p: 4,
                  minWidth: 280,
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: 6 }
                }}
                onClick={() => navigate(card.path)}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  {card.title}
                </Typography>
                <Typography variant="body1" sx={{ color: default_theme.palette.other.darkBlue, mb: 2 }}>
                  {card.description}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {card.tags.map((tag, i) => (
                    <Box key={i} className={`${styles.filterTag} ${selectedTags.includes(tag) ? styles.selected : ''}`}>{tag}</Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default RuralDevelopmentPage; 